import type { Product, CountryKey, BlogPost, Faq } from '~/types';
import type { TouristInformationCenter, WebSite, WebPage, AboutPage, Thing, ContactPage, Product as ProductSchema, AggregateRating, BreadcrumbList, ListItem, Blog, BlogPosting, FAQPage, Question } from 'schema-dts';
import { buildArticlePath, buildAuthorPath, buildPathAncestry, getLastInPath } from '~/helpers/routing';
import { getAuthorName, getFeaturedImageSrc } from '~/helpers/blog';
import useFaq from './use-faq';
import { getCountryImages } from '~/helpers/image-maps';

type Type = 'global' | 'products' | 'about' | 'contact' | 'blog' | 'article';
type Rating = [number, number];
interface Config {
  products?: Product[];
  post?: BlogPost;
}

const RATINGS: Record<CountryKey, Rating> = {
  india: [4.9, 21],
  canada: [4.9, 16],
  sri_lanka: [4.9, 11],
  morocco: [4.8, 12],
  vietnam: [4.9, 19],
  new_zealand: [5, 10],
  usa: [5, 19],
  korea: [4.8, 13],
  cambodia: [4.9, 12],
  israel: [4.8, 6],
  saudi_arabia: [4.8, 5],
  bahrain: [4.6, 6],
  indonesia: [4.7, 7],
  uk: [4.7, 11],
  togo: [4.8, 4],
  thailand: [4.7, 15],
  schengen: [4.6, 9]
};

export default function useSchemaOrg(type: Type, config?: Config) {
  const { currency, phone, socialLinks, address } = useBranch();
  const { tt } = useLocalization();
  const { locale, tProductLabel } = useLocalization();
  const route = useRoute();
  const { hostUrl, buildUrl, buildVisaCountryUrl } = useRouting();

  if (type == 'products') {
    var { country } = config!.products![0];
    var { faqs } = useFaq(country, config!.products!);
  }

  useHead({
    script: computed(() => getSchemas().map(buildScriptFromSchema))
  });


  function getSchemas(): Thing[] {
    switch (type) {
      case 'global': return [buildOrganizationSchema(), buildWebsiteSchema(), buildBreadcrumbsSchema()];
      case 'products': return [...buildProductSchemas(), ...(faqs.length ? [buildFaqPageSchema()] : [])];
      case 'contact': return [buildContactSchema()];
      case 'about': return [buildAboutSchema()];
      case 'blog': return [buildBlogSchema()];
      case 'article': return [buildArticleSchema()];
      default: throw new Error(`Bad schema type: "${type}"`);
    }
  }

  function buildArticleSchema(): BlogPosting {
    const { title, slug, excerpt, date, author } = config!.post!;
    return {
      '@type': 'BlogPosting',
      headline: title.rendered,
      description: excerpt.rendered,
      datePublished: date,
      author: {
        '@type': 'Person',
        name: getAuthorName(config!.post!),
        url: buildUrl(buildAuthorPath(author))
      },
      publisher: buildOrganizationSchema(),
      image: {
        '@type': 'ImageObject',
        url: getFeaturedImageSrc(config!.post!, 'large')
      },
      url: buildUrl(buildArticlePath(slug)),
      isPartOf: buildBlogSchema()
    };
  }
  function buildBlogSchema(): Blog {
    return {
      '@type': 'Blog',
      name: `${tt('brand.name')} — ${tt('blog.blog')}`,
      publisher: buildOrganizationSchema()
    };
  }
  function buildFaqPageSchema(): FAQPage {
    return {
      '@type': 'FAQPage',
      mainEntity: faqs.map(buildFaqSchema)
    };
  }
  function buildFaqSchema({ q, a }: Faq): Question {
    return {
      "@type": "Question",
      name: q,
      acceptedAnswer: {
        "@type": "Answer",
        text: a
      }
    };
  }
  function buildProductSchemas(): ProductSchema[] {
    return config!.products!.map(buildProductSchema);
  }
  function buildProductSchema(product: Product): ProductSchema {
    const img = getCountryImages(country).hero;
    return {
      '@type': 'Product',
      name: tProductLabel(product.name, 'long'),
      offers: {
        "@type": 'Offer',
        price: product.price[currency],
        url: buildVisaCountryUrl(country),
        priceCurrency: currency.toUpperCase(),
        availability: 'InStock'
      },
      image: {
        "@type": 'ImageObject',
        url: buildUrl(img.src),
        width: img.width!.toString(),
        height: img.height!.toString()
      },
      hasMerchantReturnPolicy: {
        "@type": 'MerchantReturnPolicy',
        refundType: 'FullRefund'
      },
      aggregateRating: buildAggregateRatingSchema()
    };
  }
  function buildAggregateRatingSchema(): AggregateRating {
    const [rating, count] = RATINGS[country];
    return {
      '@type': 'AggregateRating',
      ratingValue: rating,
      ratingCount: count
    };
  }
  function buildContactSchema(): ContactPage {
    return {
      '@type': 'ContactPage',
      mainEntityOfPage: buildWebPageSchema('contact')
    };
  }
  function buildAboutSchema(): AboutPage {
    return {
      '@type': 'AboutPage',
      mainEntityOfPage: buildWebPageSchema('about')
    };
  }
  function buildWebPageSchema(page: string): WebPage {
    return {
      "@type": 'WebPage',
      "@id": buildUrl(page),
      publisher: buildOrganizationSchema()
    };
  }
  function buildWebsiteSchema(): WebSite {
    return {
      '@type': 'WebSite',
      url: hostUrl,
      name: tt('brand.name'),
      inLanguage: locale.value
    };
  }
  function buildOrganizationSchema(): TouristInformationCenter {
    const { line, city, country, postalCode } = address;
    return {
      '@type': 'TouristInformationCenter',
      name: tt('brand.name'),
      url: hostUrl,
      logo: buildUrl('images/icon.svg'),
      telephone: phone.raw,
      sameAs: Object.values(socialLinks),
      address: {
        '@type': 'PostalAddress',
        streetAddress: line,
        addressRegion: city,
        addressCountry: country,
        postalCode
      },
      openingHoursSpecification: [{
        "@type": "OpeningHoursSpecification",
        dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
        opens: "00:00",
        closes: "23:59"
      }]
    };
  }
  function buildBreadcrumbsSchema(): BreadcrumbList {
    return {
      '@type': 'BreadcrumbList',
      itemListElement: buildPathAncestry(route.path).map(buildBreadcrumbsItemSchema)
    };
  }
  function buildBreadcrumbsItemSchema(path: string, i: number): ListItem {
    return {
      '@type': 'ListItem',
      position: i + 1,
      item: {
        name: getLastInPath(path) || 'Home',
        '@id': buildUrl(path)
      }
    }
  }
}

function buildScriptFromSchema(schema: Thing) {
  return {
    type: 'application/ld+json',
    innerHTML: JSON.stringify({
    "@context": "https://schema.org",
      ...schema as object
    })
  };
}